import { useId } from "react";
import type { InputHTMLAttributes } from "react";

import "./Campo.css";

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  rotulo: string;
  /** Mensagem de erro. Quando presente, o campo é marcado como inválido. */
  erro?: string | undefined;
  /** Texto de apoio abaixo do campo — some quando há erro. */
  dica?: string | undefined;
}

/**
 * Campo de formulário com rótulo, erro e apoio — os três amarrados.
 *
 * <p>O erro é ligado ao input por `aria-describedby` e vem **com texto**.
 * Sinalizar erro só com a cor da borda deixa de fora quem não distingue
 * as cores e quem usa leitor de tela — e num sistema onde o dado
 * digitado vira registro regulado, "o campo estava vermelho" não é
 * comunicação suficiente.
 *
 * <p>O rótulo é um `<label>` de verdade, não um `placeholder`: placeholder
 * some quando se começa a digitar, e quem se distrai no meio do
 * formulário perde a referência.
 */
export function Campo({ rotulo, erro, dica, required, ...resto }: Props) {
  const id = useId();
  const idErro = `${id}-erro`;
  const idDica = `${id}-dica`;

  const descrito = erro ? idErro : dica ? idDica : undefined;

  return (
    <div className="ui-campo">
      <label className="ui-campo__rotulo" htmlFor={id}>
        {rotulo}
        {required === true && <span aria-hidden="true"> *</span>}
      </label>

      <input
        {...resto}
        id={id}
        required={required}
        className={`ui-campo__entrada${erro ? " ui-campo__entrada--erro" : ""}`}
        aria-invalid={erro ? true : undefined}
        aria-describedby={descrito}
      />

      {erro ? (
        <p className="ui-campo__erro" id={idErro} role="alert">
          {erro}
        </p>
      ) : dica ? (
        <p className="ui-campo__dica" id={idDica}>
          {dica}
        </p>
      ) : null}
    </div>
  );
}
